import * as React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArgonBox from 'components/ArgonBox';
import ArgonButton from 'components/ArgonButton';
import { Divider, Grid, Tooltip } from '@mui/material';
import ArgonTypography from 'components/ArgonTypography';

import { useArgonController } from 'context';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import FormikNumberInput from 'components/FormikNumberInput';
import FormikCurrencyInput from 'components/FormikCurrencyInput';
import ArgonDatePicker from 'components/ArgonDatePicker';

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired
};

export default function AdaptivePricingDialog({
  open,
  onClose,
  onSave,
  adaptiveRegistrationFeeData = [],
  currency = 'EUR',
  defaultPrice = 0
}) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [controller] = useArgonController();
  const { darkMode } = controller;

  // Create validation schema for adaptive pricing
  const validationSchema = Yup.object({
    tiers: Yup.array()
      .of(
        Yup.object({
          fromRegistrations: Yup.number()
            .required('Required')
            .min(0, 'Must be 0 or greater')
            .integer('Must be a whole number'),
          fromDate: Yup.date().nullable().typeError('Invalid date'),
          registrationFee: Yup.number()
            .required('Required')
            .min(0, 'Registration fee must be 0 or greater')
        })
      )
      .test(
        'ascending-order',
        'Registration tiers must be in ascending order',
        function (tiers) {
          if (!tiers || tiers.length <= 1) return true;

          for (let i = 1; i < tiers.length; i++) {
            if (tiers[i].fromRegistrations <= tiers[i - 1].fromRegistrations) {
              return this.createError({
                path: `tiers[${i}].fromRegistrations`,
                message: 'Registration count must be greater than previous tier'
              });
            }
          }
          return true;
        }
      )
      .min(1, 'At least one tier is required')
  });

  // Initialize formik with existing data or default values
  const formik = useFormik({
    initialValues: {
      tiers:
        adaptiveRegistrationFeeData.length > 0
          ? adaptiveRegistrationFeeData.map((tier) => ({
              fromRegistrations: tier.fromRegistrations,
              fromDate: tier.fromDate || null,
              registrationFee: tier.registrationFee
            }))
          : [
              {
                fromRegistrations: 0,
                fromDate: null,
                registrationFee: defaultPrice
              }
            ]
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        // Sort tiers by fromRegistrations to ensure proper order
        const sortedTiers = [...values.tiers].sort(
          (a, b) => a.fromRegistrations - b.fromRegistrations
        );

        await onSave(sortedTiers);
        enqueueSnackbar('Adaptive pricing updated successfully!', {
          variant: 'success'
        });
        handleClose();
      } catch (error) {
        console.error('Error saving adaptive pricing:', error);
        enqueueSnackbar('Error saving adaptive pricing. Please try again.', {
          variant: 'error'
        });
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    onClose && onClose();
  };

  const handleAddTier = () => {
    const newTier = {
      fromRegistrations: 0,
      fromDate: null,
      registrationFee: defaultPrice
    };

    formik.setFieldValue('tiers', [...formik.values.tiers, newTier]);
  };

  const handleRemoveTier = (index) => {
    if (formik.values.tiers.length > 1) {
      const updatedTiers = formik.values.tiers.filter((_, i) => i !== index);
      formik.setFieldValue('tiers', updatedTiers);
    }
  };

  const TierRow = ({ tier, index }) => {
    const canRemove = formik.values.tiers.length > 1;

    return (
      <Grid
        container
        spacing={2}
        alignItems="flex-start"
        key={index}
        sx={{ mb: 3 }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <FormikNumberInput
            id={`tiers[${index}].fromRegistrations`}
            name={`tiers[${index}].fromRegistrations`}
            label={index === 0 ? 'From Registrations' : ''}
            formik={formik}
            size="small"
            integerLimit={10}
            decimalLimit={0}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <ArgonDatePicker
            id={`tiers[${index}].fromDate`}
            name={`tiers[${index}].fromDate`}
            label={index === 0 ? 'From Date (optional)' : ''}
            value={formik.values.tiers[index].fromDate}
            onChange={(date) => {
              formik.setFieldValue(`tiers[${index}].fromDate`, date[0] || null);
            }}
            options={{
              allowInput: true,
              enableTime: false
            }}
            input={{
              size: 'small',
              placeholder: 'Select date',
              error:
                formik.touched.tiers?.[index]?.fromDate &&
                Boolean(formik.errors.tiers?.[index]?.fromDate),
              helperText:
                formik.touched.tiers?.[index]?.fromDate &&
                formik.errors.tiers?.[index]?.fromDate
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormikCurrencyInput
            id={`tiers[${index}].registrationFee`}
            name={`tiers[${index}].registrationFee`}
            label={index === 0 ? 'Registration Fee' : ''}
            formik={formik}
            size="small"
            currency={currency}
            language={i18n.language}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {index === 0 && (
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              Actions
            </ArgonTypography>
          )}
          <ArgonBox display="flex" alignItems="center" mt={index === 0 ? 1 : 0}>
            <Tooltip
              title={canRemove ? 'Remove tier' : 'Cannot remove the last tier'}
            >
              <span>
                <IconButton
                  onClick={() => handleRemoveTier(index)}
                  disabled={!canRemove}
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </ArgonBox>
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      sx={{ zIndex: 1400 }}
    >
      <BootstrapDialogTitle onClose={handleClose}>
        Configure Adaptive Pricing
      </BootstrapDialogTitle>

      <DialogContent dividers style={{ maxHeight: '70vh', overflow: 'auto' }}>
        <ArgonBox>
          <ArgonTypography variant="body2" color="text" mb={3}>
            Set different prices based on the number of registrations or date.
            When EITHER the registration count reaches a tier threshold OR the
            date is reached, the corresponding price will be applied.
          </ArgonTypography>

          <form onSubmit={formik.handleSubmit}>
            {formik.values.tiers.map((tier, index) => (
              <TierRow key={index} tier={tier} index={index} />
            ))}

            <Divider sx={{ my: 2 }} />

            <ArgonBox display="flex" justifyContent="flex-start">
              <ArgonButton
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleAddTier}
                startIcon={<AddIcon />}
              >
                Add Tier
              </ArgonButton>
            </ArgonBox>
          </form>
        </ArgonBox>
      </DialogContent>

      <DialogActions>
        <ArgonBox width="100%" display="flex" justifyContent="space-between">
          <ArgonButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleClose}
          >
            Cancel
          </ArgonButton>

          <ArgonButton
            variant="gradient"
            color="success"
            size="small"
            onClick={formik.submitForm}
            disabled={!formik.isValid || formik.isSubmitting}
            loading={formik.isSubmitting}
          >
            Save Adaptive Pricing
          </ArgonButton>
        </ArgonBox>
      </DialogActions>
    </Dialog>
  );
}

AdaptivePricingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  adaptiveRegistrationFeeData: PropTypes.array,
  currency: PropTypes.string,
  defaultPrice: PropTypes.number
};
